import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { TrainingService } from '@/services/TrainingService';
import { CorpusUploadService } from '@/services/CorpusUploadService';
import type { JobSpec, SupportedModel, TrainingType } from '@/types/training';
import { selfServeTiers, type TrainingPackageCredit } from '@/lib/marketing';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  defaultName: string;
  isAdmin: boolean;
  availableCredits: TrainingPackageCredit[];
  onJobCreated: (jobId: string) => void;
};

const TRAINING_TYPES: { value: TrainingType; label: string; hint: string }[] = [
  { value: 'qlora', label: 'QLoRA (recommended)', hint: '4-bit quantized base, smallest VRAM' },
  { value: 'lora', label: 'LoRA', hint: 'No base quantization' },
  { value: 'full', label: 'Full fine-tuning', hint: 'All parameters trained, biggest VRAM' },
];

// Which training_type values each self-serve credit unlocks.
// Mirrors PACKAGE_TRAINING_TYPES in server-training.js — the proxy
// will reject mismatches; this just keeps the UI from offering options
// that the server would refuse.
const PACKAGE_TRAINING_TYPES: Record<string, TrainingType[]> = {
  'fine-tuning': ['qlora', 'lora'],
  'pre-training': ['cpt'],
  'frontier': ['qlora', 'lora', 'cpt'],
};

// Default training_type per package — what we pre-select when the user
// switches credits.
const PACKAGE_DEFAULT_TYPE: Record<string, TrainingType> = {
  'fine-tuning': 'qlora',
  'pre-training': 'cpt',
  'frontier': 'qlora',
};

// Tiers that are still beta in the backend (UI shows a badge).
const BETA_PACKAGES = new Set<string>(['pre-training', 'frontier']);

function defaultOutputName(prefix: string) {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
  return `${prefix.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${stamp}`;
}

export default function StartTrainingDialog({
  open,
  onOpenChange,
  userId,
  defaultName,
  isAdmin,
  availableCredits,
  onJobCreated,
}: Props) {

  const { toast } = useToast();
  const [models, setModels] = useState<SupportedModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [baseModel, setBaseModel] = useState<string>('');
  const [trainingType, setTrainingType] = useState<TrainingType>('qlora');
  const [outputName, setOutputName] = useState<string>(defaultOutputName(defaultName || 'model'));
  const [maxEpochs, setMaxEpochs] = useState<number>(3);
  const [contextLength, setContextLength] = useState<number>(4096);
  // GPU count for Frontier (multi-GPU) jobs. Defaulted to 2 for 27B-class
  // models, 1 otherwise — updated by the effect that watches `selected`.
  const [gpuCount, setGpuCount] = useState<number>(1);
  const [selectedCreditId, setSelectedCreditId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setModelsLoading(true);
    TrainingService.listSupportedModels()
      .then((m) => {
        setModels(m);
        if (!baseModel && m.length) {
          const phi = m.find((x) => x.id === 'microsoft/Phi-3.5-mini-instruct');
          setBaseModel((phi || m[0]).id);
        }
      })
      .catch((e) => {
        toast({
          title: 'Could not load model list',
          description: String(e?.message || e),
          variant: 'destructive',
        });
      })
      .finally(() => setModelsLoading(false));
  }, [open, baseModel, toast]);

  useEffect(() => {
    if (!open) return;
    if (!selectedCreditId && availableCredits.length > 0) {
      setSelectedCreditId(availableCredits[0].id);
    }
  }, [availableCredits, open, selectedCreditId]);

  const selected = useMemo(() => models.find((m) => m.id === baseModel) || null, [models, baseModel]);
  const isEnterpriseModel = selected?.min_tier === 'enterprise';

  // Synthetic admin-only "test as <tier>" entries that simulate having a
  // credit of each type. Picking one locks the UI exactly like a real
  // credit, but on submit we drop package_id so no real credit gets
  // consumed and the server treats it as an admin override.
  const ADMIN_TEST_PREFIX = '__admin_test_';
  const adminTestCredits = useMemo<TrainingPackageCredit[]>(() => {
    if (!isAdmin) return [];
    const now = new Date().toISOString();
    return selfServeTiers.map((tier) => ({
      id: `${ADMIN_TEST_PREFIX}${tier.id}`,
      user_id: userId || '',
      purchase_id: '',
      package_id: tier.id,
      status: 'available' as const,
      training_job_id: null,
      training_request_id: null,
      activated_at: null,
      expired_at: null,
      created_at: now,
      updated_at: now,
    }));
  }, [isAdmin, userId]);

  const displayCredits = useMemo<TrainingPackageCredit[]>(
    () => [...availableCredits, ...adminTestCredits],
    [availableCredits, adminTestCredits],
  );

  const selectedCredit = useMemo(
    () => displayCredits.find((credit) => credit.id === selectedCreditId) || null,
    [displayCredits, selectedCreditId],
  );
  const selectedCreditIsAdminTest = !!selectedCreditId && selectedCreditId.startsWith(ADMIN_TEST_PREFIX);
  const selectedTier = useMemo(
    () => selfServeTiers.find((tier) => tier.id === selectedCredit?.package_id) || null,
    [selectedCredit],
  );

  // Restrict training_type options to what the selected credit allows.
  // Admins with NO credit selected get the full menu so they can launch
  // anything. Admins who picked a synthetic test credit still respect
  // that tier's mapping (so the test is realistic).
  const allowedTrainingTypes = useMemo<TrainingType[]>(() => {
    if (selectedCredit) {
      return PACKAGE_TRAINING_TYPES[selectedCredit.package_id] || ['qlora'];
    }
    if (isAdmin) {
      return ['qlora', 'lora', 'full', 'cpt'];
    }
    return ['qlora', 'lora'];
  }, [selectedCredit, isAdmin]);

  const trainingTypeOptions = useMemo(
    () => [
      ...TRAINING_TYPES.filter((t) => allowedTrainingTypes.includes(t.value)),
      ...(allowedTrainingTypes.includes('cpt')
        ? [{ value: 'cpt' as TrainingType, label: 'Continued Pre-Training (CPT)', hint: 'Causal-LM on raw corpus' }]
        : []),
    ],
    [allowedTrainingTypes],
  );

  // When the credit changes, force training_type to a compatible value.
  // Pre-training credits lock to CPT; fine-tuning credits default to qlora.
  useEffect(() => {
    if (!selectedCredit) return;
    const defaultType = PACKAGE_DEFAULT_TYPE[selectedCredit.package_id];
    if (defaultType && !allowedTrainingTypes.includes(trainingType)) {
      setTrainingType(defaultType);
    }
  }, [selectedCredit, allowedTrainingTypes, trainingType]);

  useEffect(() => {
    if (selected && contextLength > selected.max_context) {
      setContextLength(Math.min(contextLength, selected.max_context));
    }
  }, [selected, contextLength]);

  // Default gpu_count tracks the selected base model: Frontier-tier
  // (enterprise) models default to 2 GPUs because a single A100-80GB
  // doesn't reliably fit 27B QLoRA at production-realistic context +
  // packing. Standard models stay at 1.
  useEffect(() => {
    setGpuCount(isEnterpriseModel ? 2 : 1);
  }, [isEnterpriseModel]);

  // Frontier-tier models are hidden from non-admin users unless they hold
  // a `frontier` package credit. Admins always see everything (matches the
  // existing isAdmin override on training_type).
  const visibleModels = useMemo<SupportedModel[]>(() => {
    if (isAdmin) return models;
    const hasFrontierCredit = selectedCredit?.package_id === 'frontier';
    return models.filter((m) => m.min_tier !== 'enterprise' || hasFrontierCredit);
  }, [models, isAdmin, selectedCredit]);

  // If the user had picked an enterprise model and then dropped to a
  // non-frontier credit, snap back to the first visible model.
  useEffect(() => {
    if (!baseModel) return;
    if (!visibleModels.find((m) => m.id === baseModel) && visibleModels.length > 0) {
      setBaseModel(visibleModels[0].id);
    }
  }, [visibleModels, baseModel]);

  const handleSubmit = async () => {
    if (!userId) {
      toast({
        title: 'Not signed in',
        description: 'Sign in before starting a training run.',
        variant: 'destructive',
      });
      return;
    }

    if (!baseModel || !outputName || (!selectedCredit && !isAdmin)) {
      toast({
        title: 'Training credit required',
        description: 'Purchase and select a self-serve training package before launching a run.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    const requestId = `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

    try {
      toast({ title: 'Bundling corpus…', description: 'Packaging files for upload.' });
      const upload = await CorpusUploadService.bundleAndSign({
        userId,
        requestId,
      });
      toast({
        title: 'Corpus uploaded',
        description: `${upload.meta.fileCount} file(s) → ${(upload.meta.byteCount / 1024).toFixed(1)} KB`,
      });

      // Synthetic admin-test credits must NOT send package_id — that
      // would make the proxy try to consume a real credit that doesn't
      // exist. The proxy already permits admins to launch without one.
      const packageIdForServer = selectedCreditIsAdminTest
        ? undefined
        : selectedCredit?.package_id;

      const spec: JobSpec = {
        request_id: requestId,
        dataset_uri: upload.signedUrl,
        base_model: baseModel,
        training_type: trainingType,
        output_name: outputName,
        max_epochs: maxEpochs,
        context_length: contextLength,
        validation_split: 0.1,
        learning_rate: 0.0002,
        quantize: true,
        cloud_mode: 'secure',
        // Only send gpu_count when it differs from the server-side default
        // (1). Avoids surprising older backend versions that don't know
        // the field, and keeps the request payload minimal.
        ...(gpuCount > 1 ? { gpu_count: gpuCount } : {}),
        package_id: packageIdForServer,
      };

      const job = await TrainingService.createJob(spec);
      toast({
        title: 'Training queued',
        description: selectedCreditIsAdminTest && selectedTier
          ? `Admin test (${selectedTier.name}) launched for job ${job.id.slice(0, 8)}… — no credit consumed.`
          : selectedTier
            ? `${selectedTier.name} credit is now in use for job ${job.id.slice(0, 8)}…`
            : `Admin training override launched for job ${job.id.slice(0, 8)}…`,
      });

      onJobCreated(job.id);
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: 'Could not start training',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Start sovereign training</DialogTitle>
          <DialogDescription>
            We'll upload your corpus to your private CanXP workspace and queue a training job on a CanXP GPU pod.
            The job type (fine-tuning, continued pre-training, etc.) is determined by the package credit you select.
            Non-admin users must consume a credit; admins can also launch an override without one.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Training package credit</Label>
            <Select value={selectedCreditId} onValueChange={setSelectedCreditId} disabled={displayCredits.length === 0}>

              <SelectTrigger>
                <SelectValue placeholder={displayCredits.length === 0 ? 'No package credits available' : 'Select package credit'} />
              </SelectTrigger>
              <SelectContent>
                {availableCredits.map((credit) => {
                  const tier = selfServeTiers.find((entry) => entry.id === credit.package_id);
                  const isBeta = BETA_PACKAGES.has(credit.package_id);
                  return (
                    <SelectItem key={credit.id} value={credit.id}>
                      {tier?.name || credit.package_id}
                      {isBeta ? ' (beta)' : ''} · purchased {new Date(credit.created_at).toLocaleDateString()}
                    </SelectItem>
                  );
                })}
                {adminTestCredits.map((credit) => {
                  const tier = selfServeTiers.find((entry) => entry.id === credit.package_id);
                  return (
                    <SelectItem key={credit.id} value={credit.id}>
                      Admin test: {tier?.name || credit.package_id} (no credit consumed)
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedTier && selectedCreditIsAdminTest ? (
              <p className="text-xs text-muted-foreground">
                Admin override: simulating a {selectedTier.name} credit. No credit will be consumed; the
                job runs with the same training_type the tier would unlock.
              </p>
            ) : selectedTier ? (
              <p className="text-xs text-muted-foreground">
                Using a one-time {selectedTier.name} credit purchased on {new Date(selectedCredit?.created_at || '').toLocaleString()}.
              </p>
            ) : isAdmin ? (
              <p className="text-xs text-muted-foreground">
                No credit selected. As an admin, you can launch any training type below without
                consuming a package — or pick an "Admin test" entry above to simulate a tier.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Buy a self-serve training package first if you do not see an available credit here.
              </p>
            )}

          </div>

          <div className="grid gap-2">
            <Label htmlFor="output_name">Output model name</Label>
            <Input
              id="output_name"
              value={outputName}
              onChange={(e) => setOutputName(e.target.value)}
              pattern="[a-zA-Z0-9._\-]+"
              placeholder="customer-support-v1"
            />
            <p className="text-xs text-muted-foreground">Letters, digits, dots, dashes, underscores. No spaces.</p>
          </div>

          <div className="grid gap-2">
            <Label>Base model</Label>
            <Select value={baseModel} onValueChange={setBaseModel} disabled={modelsLoading}>
              <SelectTrigger>
                <SelectValue placeholder={modelsLoading ? 'Loading…' : 'Pick a base model'} />
              </SelectTrigger>
              <SelectContent>
                {visibleModels.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                    {m.min_tier === 'enterprise' ? ' · Frontier' : ''} · {m.license}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected ? (
              <p className="text-xs text-muted-foreground">
                {isEnterpriseModel ? (
                  <span className="mr-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                    Frontier
                  </span>
                ) : null}
                {selected.parameters_b}B params · max context {selected.max_context.toLocaleString()} · min VRAM (QLoRA){' '}
                {selected.min_vram_gb_qlora} GB
                {selected.notes ? ` · ${selected.notes}` : ''}
              </p>
            ) : null}
            {isEnterpriseModel ? (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Frontier tier · multi-GPU run on 2× A100/H100 80 GB. A 1-epoch QLoRA
                pass on a typical Canadian-context corpus runs roughly $30–$60 in GPU time.
              </p>
            ) : null}
          </div>

          {isEnterpriseModel ? (
            <div className="grid gap-2">
              <Label htmlFor="gpu_count">GPU count</Label>
              <Select value={String(gpuCount)} onValueChange={(v) => setGpuCount(Number(v))}>
                <SelectTrigger id="gpu_count">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2× GPU (default)</SelectItem>
                  <SelectItem value="4">4× GPU (faster, 2× cost)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                More GPUs reduce wallclock by sharding the optimizer (DeepSpeed ZeRO-3).
                GPU-hour cost stays constant; total cost scales with count.
              </p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label>Training type</Label>
              <Select
                value={trainingType}
                onValueChange={(v) => setTrainingType(v as TrainingType)}
                disabled={trainingTypeOptions.length <= 1}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {trainingTypeOptions.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {trainingType === 'cpt' ? (
                <p className="text-xs text-muted-foreground">
                  CPT runs causal-LM next-token prediction on raw text (no chat template).
                  Best for domain specialisation on long-form corpora. <span className="font-medium">Beta.</span>
                </p>
              ) : null}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="max_epochs">Epochs</Label>
              <Input
                id="max_epochs"
                type="number"
                min={1}
                max={20}
                value={maxEpochs}
                onChange={(e) => setMaxEpochs(Number(e.target.value) || 1)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="context_length">Context length</Label>
              <Input
                id="context_length"
                type="number"
                min={256}
                max={selected?.max_context || 131072}
                step={256}
                value={contextLength}
                onChange={(e) => setContextLength(Number(e.target.value) || 4096)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !baseModel || !outputName || (!selectedCredit && !isAdmin)}>

            {submitting ? 'Submitting…' : 'Start training'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
